import React, { useState } from 'react';
import { Lock, Zap, AlertTriangle, Check, Gift, ChevronDown, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollFadeIn } from './ScrollFadeIn';
interface FinalCTAProps {
  onAddToCart?: () => void;
}
export function FinalCTA({
  onAddToCart
}: FinalCTAProps) {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  return <section className="scroll-mt-8 py-10 bg-slate-900 relative overflow-hidden" data-id="element-234">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-235" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-236">
        <ScrollFadeIn data-id="element-237">
          <h2 className="text-4xl md:text-5xl font-serif text-slate-50 mb-6" data-id="element-238">
            Stop Fighting Your Biology.
            <br data-id="element-239" />
            <span className="text-amber-500 italic" data-id="element-240">
              Start Switching It Off.
            </span>
          </h2>
        </ScrollFadeIn>

        <ScrollFadeIn delay={0.1} data-id="element-241">
          <div className="space-y-4 text-lg text-slate-300 leading-relaxed mb-4" data-id="element-242">
            <p data-id="element-243">
              Everything above — the manual, the kit, the map, and the taper
              plan — is available right now for a single payment of $6.39.
            </p>
            <p data-id="element-244">No subscriptions. No upsells. No "premium tier."</p>
            <p data-id="element-245">
              Just the complete system, delivered instantly to your inbox the
              moment you check out.
            </p>
          </div>
        </ScrollFadeIn>

        {/* Urgency warning */}
        <ScrollFadeIn delay={0.15} data-id="element-246">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6 text-left" data-id="element-247">
            <div className="flex items-start gap-3" data-id="element-248">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-249" />
              <div className="text-sm text-slate-300 leading-relaxed space-y-2" data-id="element-250">
                <p className="text-slate-50 font-medium" data-id="element-251">
                  One thing to keep in mind:
                </p>
                <p data-id="element-252">
                  The bonus stack is included at this price as part of a limited
                  launch period. Once the launch window closes, the bonuses will
                  either be sold separately or removed from the offer entirely.
                </p>
                <p className="text-amber-400 font-medium" data-id="element-253">
                  If you're reading this page, the offer is still active.
                </p>
              </div>
            </div>
          </div>
        </ScrollFadeIn>

        <ScrollFadeIn delay={0.2} data-id="element-254">
          <div id="final-cta" className="scroll-mt-4 bg-slate-800 border border-slate-700 rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-255">
            <div className="mb-6" data-id="element-256">
              <span className="text-slate-400 uppercase tracking-widest text-sm font-semibold" data-id="element-257">
                Digital Access
              </span>
              <div className="mt-3 flex items-center justify-center gap-3" data-id="element-258">
                <span className="text-2xl text-slate-500 line-through font-medium" data-id="element-259">
                  ${148 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)}
                </span>
                <motion.span key={`${bump1}-${bump2}`} initial={{
                scale: 1
              }} animate={{
                scale: [1, 1.08, 1]
              }} transition={{
                duration: 0.35
              }} className="text-5xl font-bold text-slate-50" data-id="element-260">
                  ${(6.39 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)).toFixed(2)}
                </motion.span>
              </div>
              <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30" data-id="element-261">
                <span className="text-emerald-400 text-sm font-semibold tracking-wide" data-id="element-262">
                  {Math.round((1 - (6.39 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)) / (148 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0))) * 100)}
                  % OFF — Launch Price
                </span>
              </div>
            </div>

            {/* Bonus stack visual */}
            <div className="border-t border-slate-700 pt-6 mb-6" data-id="element-263">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-4" data-id="element-264">
                What's included:
              </p>

              {/* Main eBook — prominent */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/60 border border-amber-500/20 mb-4" data-id="element-265">
                <img src="https://cdn.magicpatterns.com/uploads/7wUxyExPymVP8TFY7ndcVW/The-Cortisol-Switch-Logo.png" alt="The Cortisol Switch eBook" className="w-20 h-auto rounded-lg shadow-md shrink-0" data-id="element-266" />
                <div className="text-left" data-id="element-267">
                  <span className="text-sm text-slate-50 font-semibold" data-id="element-268">
                    The Cortisol Switch
                  </span>
                  <p className="text-xs text-slate-400" data-id="element-269">
                    Complete eBook — The full protocol
                  </p>
                </div>
              </div>

              {/* Bonuses — row below */}
              <div className="grid grid-cols-3 gap-3" data-id="element-270">
                {/* Bonus 1 */}
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-271">
                  <img src="https://cdn.magicpatterns.com/uploads/rcZcghPkokYckJZzRYhPcJ/3AM-Rescue-Kit.png" alt="The 3AM Rescue Kit" className="w-14 h-auto rounded shadow-md" data-id="element-272" />
                  <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-273">
                    3AM Rescue Kit
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-274">
                    <Gift className="w-3 h-3" data-id="element-275" /> FREE
                  </span>
                </div>

                {/* Bonus 2 */}
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-276">
                  <img src="https://cdn.magicpatterns.com/uploads/aDH1GgafDem32o1ZEft9G8/Cortisol-Safe-Grocery-Map.png" alt="Cortisol-Safe Grocery Map" className="w-14 h-auto rounded shadow-md" data-id="element-277" />
                  <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-278">
                    Grocery Map
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-279">
                    <Gift className="w-3 h-3" data-id="element-280" /> FREE
                  </span>
                </div>

                {/* Bonus 3 */}
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-281">
                  <img src="https://cdn.magicpatterns.com/uploads/cuSNPLuFnNdPTDBYMEMsKM/Coffee-Exit-Plan.png" alt="The Coffee Exit Plan" className="w-14 h-auto rounded shadow-md" data-id="element-282" />
                  <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-283">
                    Coffee Exit Plan
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-284">
                    <Gift className="w-3 h-3" data-id="element-285" /> FREE
                  </span>
                </div>
              </div>

              {/* Read more toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors font-medium" data-id="element-286">
                {showBonusDetails ? 'Show less' : 'Read more about the bonuses'}
                <motion.span animate={{
                rotate: showBonusDetails ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className="inline-flex" data-id="element-287">
                  <ChevronDown className="w-4 h-4" data-id="element-288" />
                </motion.span>
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-289">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: [0.21, 0.47, 0.32, 0.98]
              }} className="overflow-hidden" data-id="element-290">
                    <div className="pt-6 space-y-6 text-left" data-id="element-291">
                      {/* Bonus 1 — 3AM Rescue Kit */}
                      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-292">
                        <div data-id="element-293">
                          <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-294">
                            <Gift className="w-3.5 h-3.5" data-id="element-295" />
                            <span data-id="element-296">Bonus #1</span>
                          </div>
                          <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-297">
                            The 3AM Rescue Kit
                          </h4>
                          <p className="text-slate-400 text-xs" data-id="element-298">
                            Fast-Back-to-Sleep Protocol —{' '}
                            <span className="text-emerald-400 font-semibold" data-id="element-299">
                              FREE
                            </span>
                          </p>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed" data-id="element-300">
                          A decision-based rapid-response system for when you
                          wake at 3AM. Instead of guessing what to do
                          half-asleep, follow a simple 3-route decision tree:
                        </p>
                        <div className="space-y-2" data-id="element-301">
                          <div className="flex items-start gap-2.5" data-id="element-302">
                            <div className="w-4 h-4 rounded-full bg-blue-400 shrink-0 mt-0.5" data-id="element-303" />
                            <p className="text-slate-300 text-xs" data-id="element-304">
                              <span className="text-slate-50 font-medium" data-id="element-305">
                                Route A:
                              </span>{' '}
                              Heart racing — 3-step physical reset
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-306">
                            <div className="w-4 h-4 rounded-full bg-amber-400 shrink-0 mt-0.5" data-id="element-307" />
                            <p className="text-slate-300 text-xs" data-id="element-308">
                              <span className="text-slate-50 font-medium" data-id="element-309">
                                Route B:
                              </span>{' '}
                              Real hunger — rescue snack rules
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-310">
                            <div className="w-4 h-4 rounded-full bg-emerald-400 shrink-0 mt-0.5" data-id="element-311" />
                            <p className="text-slate-300 text-xs" data-id="element-312">
                              <span className="text-slate-50 font-medium" data-id="element-313">
                                Route C:
                              </span>{' '}
                              Mind looping — 4-line journal script
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1.5" data-id="element-314">
                          {['60-second decision tree (usable half-asleep)', '4-7-8 Vagal Brake sequence', 'Rescue snack rules', 'Anti-rumination script', '7 mistakes that keep you awake', 'Printable nightstand one-pager'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-315">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-316" />
                              <span className="text-slate-300 text-xs" data-id="element-317">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      {/* Bonus 2 — Grocery Map */}
                      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-318">
                        <div data-id="element-319">
                          <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-320">
                            <Gift className="w-3.5 h-3.5" data-id="element-321" />
                            <span data-id="element-322">Bonus #2</span>
                          </div>
                          <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-323">
                            Cortisol-Safe Grocery Map
                          </h4>
                          <p className="text-slate-400 text-xs" data-id="element-324">
                            Done-for-You Shopping List + Night Staples (14 Days)
                            —{' '}
                            <span className="text-emerald-400 font-semibold" data-id="element-325">
                              FREE
                            </span>
                          </p>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed" data-id="element-326">
                          A shopping system — not a meal plan. One trip to the
                          store sets you up for two full weeks of calmer
                          evenings and steadier sleep.
                        </p>
                        <div className="space-y-1.5" data-id="element-327">
                          {['12 Night Staples for blood sugar stability', 'Aisle-by-aisle shopping list', 'Cortisol-Safe Dinner Formula', '14 simple dinner + snack combos (10-15 min)', 'Dairy-free, gluten-free & vegetarian swaps', '"Better to Avoid" trigger food list', '"Your First Cart" fill-in template'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-328">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-329" />
                              <span className="text-slate-300 text-xs" data-id="element-330">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      {/* Bonus 3 — Coffee Exit Plan */}
                      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-331">
                        <div data-id="element-332">
                          <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-333">
                            <Gift className="w-3.5 h-3.5" data-id="element-334" />
                            <span data-id="element-335">Bonus #3</span>
                          </div>
                          <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-336">
                            The Coffee Exit Plan
                          </h4>
                          <p className="text-slate-400 text-xs" data-id="element-337">
                            2-Week Caffeine Taper Without the Crash —{' '}
                            <span className="text-emerald-400 font-semibold" data-id="element-338">
                              FREE
                            </span>
                          </p>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed" data-id="element-339">
                          Breaks the caffeine-cortisol loop without quitting
                          cold turkey. 14 days, 3 simple phases:
                        </p>
                        <div className="space-y-2" data-id="element-340">
                          <div className="flex items-start gap-2.5" data-id="element-341">
                            <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-342" />
                            <p className="text-slate-300 text-xs" data-id="element-343">
                              <span className="text-slate-50 font-medium" data-id="element-344">
                                Days 1-3:
                              </span>{' '}
                              Timing reset
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-345">
                            <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-346" />
                            <p className="text-slate-300 text-xs" data-id="element-347">
                              <span className="text-slate-50 font-medium" data-id="element-348">
                                Days 4-7:
                              </span>{' '}
                              Gentle taper
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-349">
                            <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-350" />
                            <p className="text-slate-300 text-xs" data-id="element-351">
                              <span className="text-slate-50 font-medium" data-id="element-352">
                                Days 8-14:
                              </span>{' '}
                              Stabilize
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1.5" data-id="element-353">
                          {['4 Timing Rules for jitters & crashes', 'Cut-off time calculator', '10 non-caffeine energy boosters', '2PM Crash Protocol (5 min fix)', 'Headache & irritability troubleshooting', '14-day daily tracker'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-354">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-355" />
                              <span className="text-slate-300 text-xs" data-id="element-356">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Confirmation line */}
            <div className="flex items-center justify-center gap-2 mb-6" data-id="element-357">
              <Check className="w-5 h-5 text-emerald-400" data-id="element-358" />
              <span className="text-emerald-400 font-semibold" data-id="element-359">
                YES — I want The Cortisol Switch + all 3 bonuses
              </span>
            </div>

            <a href={(() => {
            // Base product only (no bumps)
            if (!bump1 && !bump2) {
              return 'https://www.recursosencalma.com/cart/45769486663853:1';
            }
            // Base + Bump 1 only
            if (bump1 && !bump2) {
              return 'https://recursosencalma.com/cart/45844404961453:1';
            }
            // Base + Bump 2 only
            if (!bump1 && bump2) {
              return 'https://recursosencalma.com/cart/45844411449517:1';
            }
            // Base + Both bumps
            if (bump1 && bump2) {
              return 'https://recursosencalma.com/cart/45844417872045:1';
            }
            // Fallback
            return 'https://www.recursosencalma.com/cart/45769486663853:1';
          })()} target="_blank" rel="noopener noreferrer" onClick={onAddToCart} className="cta-pulse w-full sm:w-auto inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-slate-900 bg-amber-500 rounded-xl hover:bg-amber-400 transition-colors duration-300 mb-4" data-id="element-360">
              Download The Cortisol Switch Now
            </a>

            <p className="text-sm text-slate-300 mb-6 flex items-center justify-center gap-2" data-id="element-361">
              <span className="text-amber-400 text-lg" data-id="element-362">👇</span>
              <span className="italic font-medium" data-id="element-363">
                Want faster results? Check out these exclusive add‑ons below
              </span>
              <span className="text-amber-400 text-lg" data-id="element-364">👇</span>
            </p>

            {/* Highly Recommended badge */}
            <div className="flex items-center justify-center gap-2 mb-3" data-id="element-365">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-366" />
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-400 px-3 py-1 rounded-full border border-amber-500/25 bg-amber-500/5" data-id="element-367">
                ⭐ Highly Recommended
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-368" />
            </div>

            {/* ORDER BUMP #1 */}
            <div onClick={() => setBump1(!bump1)} className={`bump-shimmer text-left rounded-xl p-5 mb-4 cursor-pointer transition-all duration-300 border-2 ${bump1 ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_20px_rgba(245,158,11,0.1)]' : 'border-amber-500/30 bg-slate-900/60 hover:border-amber-500/60'}`} data-id="element-369">
              <div className="flex items-start gap-4" data-id="element-370">
                <div className="shrink-0 mt-1" data-id="element-371">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${bump1 ? 'bg-amber-500 border-amber-500' : 'border-slate-500'}`} data-id="element-372">
                    {bump1 && <Check className="w-3.5 h-3.5 text-slate-900" data-id="element-373" />}
                  </div>
                </div>
                <div className="flex-1" data-id="element-374">
                  <div className="flex items-start gap-3 mb-3" data-id="element-375">
                    <img src="https://cdn.magicpatterns.com/uploads/fxsWhHWinb4ud5Fwv8s2as/The-Cortisol-Elite-Motion-Bible.png" alt="The Cortisol-Safe Elite Motion Bible" className="w-16 h-auto rounded-lg shadow-md shrink-0" data-id="element-376" />
                    <div data-id="element-377">
                      <p className="text-sm text-slate-50 font-bold leading-snug" data-id="element-378">
                        The Cortisol‑Safe Elite Motion Bible
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5" data-id="element-379">
                        Desk Resets + At-Home Sculpt Routine
                      </p>
                      <p className="mt-1" data-id="element-380">
                        <span className="text-lg text-amber-400 font-bold" data-id="element-381">
                          $9
                        </span>{' '}
                        <span className="text-emerald-400 text-xs font-semibold" data-id="element-382">
                          (Save 90%)
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-slate-400 leading-relaxed" data-id="element-383">
                    <p data-id="element-384">
                      Stop "punishing" your body with high-intensity workouts
                      that only spike your cortisol and keep your belly fat
                      locked in place. Most people over 40 are accidentally
                      over-stressing their hearts and glands in the gym.
                    </p>
                    <p data-id="element-385">
                      Inside this Elite Playbook, you'll discover the 2-minute
                      "Desk-Resets" to drain your lymphatic system during a
                      conference call, plus a specialized At-Home "Sculpt"
                      Routine that builds lean muscle without triggering the
                      stress-response.
                    </p>
                    <p className="text-slate-300" data-id="element-386">
                      Learn how to move with intensity while keeping your
                      nervous system in a state of "Total Calm." Get the body
                      without the burnout for less than a movie ticket.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Highly Recommended badge for bump #2 */}
            <div className="flex items-center justify-center gap-2 mb-3" data-id="element-387">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-388" />
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-400 px-3 py-1 rounded-full border border-amber-500/25 bg-amber-500/5" data-id="element-389">
                ⭐ Highly Recommended
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-390" />
            </div>

            {/* ORDER BUMP #2 */}
            <div onClick={() => setBump2(!bump2)} className={`bump-shimmer text-left rounded-xl p-5 mb-6 cursor-pointer transition-all duration-300 border-2 ${bump2 ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_20px_rgba(245,158,11,0.1)]' : 'border-amber-500/30 bg-slate-900/60 hover:border-amber-500/60'}`} data-id="element-391">
              <div className="flex items-start gap-4" data-id="element-392">
                <div className="shrink-0 mt-1" data-id="element-393">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${bump2 ? 'bg-amber-500 border-amber-500' : 'border-slate-500'}`} data-id="element-394">
                    {bump2 && <Check className="w-3.5 h-3.5 text-slate-900" data-id="element-395" />}
                  </div>
                </div>
                <div className="flex-1" data-id="element-396">
                  <div className="flex items-start gap-3 mb-3" data-id="element-397">
                    <img src="https://cdn.magicpatterns.com/uploads/1WsdHs7Nz6EJrKhZPEVY83/Stress-Proof-Dining-Out-Guide.png" alt="Stress-Proof Dining Out Guide" className="w-16 h-auto rounded-lg shadow-md shrink-0" data-id="element-398" />
                    <div data-id="element-399">
                      <p className="text-sm text-slate-50 font-bold leading-snug" data-id="element-400">
                        Stress‑Proof Dining Out Guide
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5" data-id="element-401">
                        Safe Orders + Social Buffer Strategy
                      </p>
                      <p className="mt-1" data-id="element-402">
                        <span className="text-lg text-amber-400 font-bold" data-id="element-403">
                          $9
                        </span>{' '}
                        <span className="text-amber-400 text-xs font-semibold" data-id="element-404">
                          (One‑time offer)
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-slate-400 leading-relaxed" data-id="element-405">
                    <p data-id="element-406">
                      Life doesn't pause while you're resetting your
                      metabolism—and you shouldn't have to fear the menu.
                      Whether it's a business dinner, a weekend celebration, or
                      an airport layover, this guide helps you stay in
                      "Fat-Burning Mode" without being the "difficult person" at
                      the table.
                    </p>
                    <p data-id="element-407">
                      This Mobile-Optimized Pocket Guide gives you elegant
                      "Safe-Orders" for Italian, Mexican, Steakhouses, and even
                      Fast-Casual spots—plus the "Social Buffer" strategy for
                      enjoying a drink without triggering the dreaded 3 AM
                      wake-up call later.
                    </p>
                    <p className="text-slate-300" data-id="element-408">
                      Keep your social life AND your results. Consider this your
                      biological insurance policy for the real world.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-amber-500/70 font-medium mb-4 italic" data-id="element-409">
              One-time offer. This price is only available now.
            </p>

            {/* DYNAMIC ORDER SUMMARY */}
            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5 mb-6 text-left" data-id="element-410">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3" data-id="element-411">
                🛒 Your Order Summary
              </p>
              <div className="space-y-2" data-id="element-412">
                {/* Base product - always included */}
                <div className="flex items-center justify-between text-sm" data-id="element-413">
                  <span className="text-slate-300" data-id="element-414">
                    The Cortisol Switch + 3 Bonuses
                  </span>
                  <span className="text-slate-50 font-semibold" data-id="element-415">$6.39</span>
                </div>

                {/* Bump 1 */}
                <AnimatePresence data-id="element-416">
                  {bump1 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="flex items-center justify-between text-sm overflow-hidden" data-id="element-417">
                      <span className="text-slate-300" data-id="element-418">Elite Motion Bible</span>
                      <span className="text-amber-400 font-semibold" data-id="element-419">
                        $9.00
                      </span>
                    </motion.div>}
                </AnimatePresence>

                {/* Bump 2 */}
                <AnimatePresence data-id="element-420">
                  {bump2 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="flex items-center justify-between text-sm overflow-hidden" data-id="element-421">
                      <span className="text-slate-300" data-id="element-422">Dining Out Guide</span>
                      <span className="text-amber-400 font-semibold" data-id="element-423">
                        $9.00
                      </span>
                    </motion.div>}
                </AnimatePresence>

                {/* Divider */}
                <div className="border-t border-slate-700 pt-2 mt-2 flex items-center justify-between" data-id="element-424">
                  <span className="text-slate-50 font-bold text-sm" data-id="element-425">Total</span>
                  <motion.span key={`summary-${bump1}-${bump2}`} initial={{
                  scale: 1
                }} animate={{
                  scale: [1, 1.1, 1]
                }} transition={{
                  duration: 0.3
                }} className="text-xl font-bold text-amber-400" data-id="element-426">
                    ${(6.39 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)).toFixed(2)}
                  </motion.span>
                </div>
              </div>

              {/* Dynamic Buy Button */}
              <a href={(() => {
              // Base product only (no bumps)
              if (!bump1 && !bump2) {
                return 'https://www.recursosencalma.com/cart/45769486663853:1';
              }
              // Base + Bump 1 only
              if (bump1 && !bump2) {
                return 'https://recursosencalma.com/cart/45844404961453:1';
              }
              // Base + Bump 2 only
              if (!bump1 && bump2) {
                return 'https://recursosencalma.com/cart/45844411449517:1';
              }
              // Base + Both bumps
              if (bump1 && bump2) {
                return 'https://recursosencalma.com/cart/45844417872045:1';
              }
              // Fallback (should never reach here)
              return 'https://www.recursosencalma.com/cart/45769486663853:1';
            })()} target="_blank" rel="noopener noreferrer" onClick={onAddToCart} className="cta-pulse mt-4 w-full inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-slate-900 bg-amber-500 rounded-xl hover:bg-amber-400 transition-colors duration-300 text-center" data-id="element-427">
                Download The Cortisol Switch
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 text-sm text-slate-400 mb-4" data-id="element-428">
              <div className="flex items-center" data-id="element-429">
                <Lock className="w-4 h-4 mr-2 text-emerald-400" data-id="element-430" />
                Secure Checkout
              </div>
              <div className="hidden sm:block text-slate-600" data-id="element-431">•</div>
              <div className="flex items-center" data-id="element-432">
                <Zap className="w-4 h-4 mr-2 text-amber-400" data-id="element-433" />
                Immediate Digital Access
              </div>
            </div>

            <p className="text-xs text-slate-500" data-id="element-434">
              Secure checkout. Immediate digital delivery. 100% satisfaction
              guaranteed.
            </p>
          </div>
        </ScrollFadeIn>
      </div>
    </section>;
}